import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Briefcase, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Plumbing", "HVAC", "Electrical", "Glass", "Locksmith", "General contractor", "Cleaning", "Other"];

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "Plumbing", phone: "", email: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vendors");
      setVendors(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to load");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    setSaving(true);
    try {
      await api.post("/vendors", { ...form, name: form.name.trim() });
      toast.success("Vendor added");
      setForm({ name: "", category: "Plumbing", phone: "", email: "", notes: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Failed to create");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this vendor? Past issues will retain the vendor name.")) return;
    try {
      await api.delete(`/vendors/${id}`);
      toast.success("Vendor removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e) || "Delete failed");
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-gray-500 mb-2">Admin</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Vendors</h1>
            <p className="text-sm text-gray-600 mt-2">
              Outside contractors you can attach to an issue when logging cost.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0055FF] hover:bg-[#0044CC] h-11" data-testid="add-vendor-btn">
                <Plus size={16} className="mr-1" /> New vendor
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display tracking-tight text-2xl">New vendor</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Name</Label>
                    <Input
                      data-testid="vendor-name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Acme Plumbing"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Category</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="vendor-category"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Phone</Label>
                    <Input
                      data-testid="vendor-phone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Email</Label>
                    <Input
                      data-testid="vendor-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="ops@acme.com"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-gray-700">Notes</Label>
                  <Input
                    data-testid="vendor-notes"
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button data-testid="save-vendor" className="bg-[#0055FF] hover:bg-[#0044CC]" disabled={saving} onClick={create}>
                  Create
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="vendors-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendor</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {vendors.map((v) => (
                <TableRow key={v.id} data-testid={`vendor-row-${v.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-md bg-[#0055FF]/10 text-[#0055FF] flex items-center justify-center">
                        <Briefcase size={14} />
                      </div>
                      {v.name}
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-600">{v.category || "—"}</TableCell>
                  <TableCell className="text-gray-600">
                    <div className="space-y-0.5 text-xs">
                      {v.phone && <div className="flex items-center gap-1"><Phone size={10} />{v.phone}</div>}
                      {v.email && <div className="flex items-center gap-1"><Mail size={10} />{v.email}</div>}
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-500 text-xs">{v.notes || "—"}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost" size="icon"
                      data-testid={`delete-vendor-${v.id}`}
                      onClick={() => remove(v.id)}
                      className="text-gray-400 hover:text-red-600"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {vendors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-gray-500 py-8">
                    No vendors yet. Add the contractors you regularly call.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
}
